import { NOMBRE_ESTADO_ACTIVIDAD, NOMBRE_ESTADO_MOSTRADOR, NOMBRE_ESTADO_PARTICIPACION, NOMBRE_ORIGEN_DATOS } from '@planilla/contratos';
import type { EstadoActividad, EstadoMostrador } from '@planilla/dominio';

/**
 * UNA PALABRA POR CONCEPTO — y la palabra la elige el contrato
 * ===========================================================
 *
 * Cómo se llama cada estado sale de `@planilla/contratos` (`NOMBRE_ESTADO_*`), que es el mismo
 * lugar del que lo lee la API. Lo que se agrega acá es solo lo VISUAL: con qué tono del semáforo
 * y con qué ícono se dibuja cada estado, más el titular corto del mostrador.
 *
 * Por qué hay un titular aparte: el veredicto se lee a un metro de distancia, en el cuerpo de
 * `--texto-veredicto`: tiene que ser corto (NO PAGADO, YA COBRÓ, NO REGISTRADO). La frase completa
 * va como tooltip del cartel, no como subtítulo: en el mostrador el texto de más estorba
 * (Rodrigo, 2026-09-26).
 */

/** Los tonos son los del semáforo de la capa de tokens. No hay otros. */
export type Tono = 'pendiente' | 'pagado' | 'ausente' | 'anulado' | 'alerta' | 'exito' | 'informacion';

interface Vestido {
  /** La palabra, siempre la misma, tomada del contrato. */
  palabra: string;
  tono: Tono;
  icono: string;
}

/**
 * EL VEREDICTO DEL MOSTRADOR. Tres respuestas, cada una con color + ícono + PALABRA: el color solo
 * no alcanza (daltonismo, brillo de pantalla, mirar de reojo) y acá una confusión cuesta plata.
 *
 * "Ya cobró" es ámbar y no verde a propósito: el verde dice "adelante" y este es justo el caso en
 * que hay que FRENAR.
 */
export const VEREDICTO: Record<EstadoMostrador, Vestido & { titular: string; explicacion: string }> = {
  PENDIENTE: {
    palabra: NOMBRE_ESTADO_MOSTRADOR.PENDIENTE,
    titular: 'NO PAGADO',
    explicacion: 'Está en la lista de esta actividad y todavía no cobró.',
    tono: 'pendiente',
    icono: 'dinero',
  },
  PAGADO: {
    palabra: NOMBRE_ESTADO_MOSTRADOR.PAGADO,
    titular: 'YA COBRÓ',
    explicacion: 'Esta persona ya recibió el pago de esta actividad. No le pagues de nuevo.',
    tono: 'pagado',
    icono: 'alto',
  },
  NO_INSCRIPTO: {
    palabra: NOMBRE_ESTADO_MOSTRADOR.NO_INSCRIPTO,
    titular: 'NO REGISTRADO',
    explicacion: 'Está registrado en la entidad, pero no en esta actividad.',
    tono: 'ausente',
    icono: 'buscar',
  },
  SIN_REGISTRO: {
    palabra: NOMBRE_ESTADO_MOSTRADOR.SIN_REGISTRO,
    titular: 'NO REGISTRADO',
    explicacion: 'Esta cédula no está registrada.',
    tono: 'ausente',
    icono: 'buscar',
  },
};

/** El estado de una participación: las mismas dos palabras que usa el mostrador. */
export const ESTADO_PARTICIPACION: Record<'PENDIENTE' | 'PAGADO', Vestido> = {
  PENDIENTE: { palabra: NOMBRE_ESTADO_PARTICIPACION.PENDIENTE, tono: 'pendiente', icono: 'dinero' },
  PAGADO: { palabra: NOMBRE_ESTADO_PARTICIPACION.PAGADO, tono: 'pagado', icono: 'check' },
};

/**
 * Los estados de una actividad, con la CONSECUENCIA de cada cambio escrita al lado: el cartel de
 * confirmación tiene que decir qué va a pasar, no preguntar "¿estás seguro?".
 */
export const ESTADO_ACTIVIDAD: Record<EstadoActividad, Vestido & { consecuencia: string }> = {
  BORRADOR: {
    palabra: NOMBRE_ESTADO_ACTIVIDAD.BORRADOR,
    tono: 'informacion',
    icono: 'editar',
    consecuencia: 'Todavía no aparece en el mostrador: tocá Abrir.',
  },
  ABIERTA: {
    palabra: NOMBRE_ESTADO_ACTIVIDAD.ABIERTA,
    tono: 'pendiente',
    icono: 'mostrador',
    consecuencia: 'Abierta: ya aparece en el mostrador para pagar.',
  },
  CERRADA: {
    palabra: NOMBRE_ESTADO_ACTIVIDAD.CERRADA,
    tono: 'ausente',
    icono: 'candado',
    consecuencia: 'Cerrada: no se le paga a nadie más.',
  },
  ANULADA: {
    palabra: NOMBRE_ESTADO_ACTIVIDAD.ANULADA,
    tono: 'anulado',
    icono: 'anular',
    consecuencia: 'Borrada.',
  },
};

export const ESTADO_PAGO: Record<'VIGENTE' | 'ANULADO', Vestido> = {
  VIGENTE: { palabra: NOMBRE_ESTADO_PARTICIPACION.PAGADO, tono: 'pagado', icono: 'check' },
  ANULADO: { palabra: 'Anulado', tono: 'anulado', icono: 'anular' },
};

export const ORIGEN_DATOS: Record<string, string> = NOMBRE_ORIGEN_DATOS;

export const ESTADO_ENTIDAD: Record<string, Vestido> = {
  ACTIVA: { palabra: 'Activa', tono: 'exito', icono: 'check' },
  SUSPENDIDA: { palabra: 'Suspendida', tono: 'alerta', icono: 'alerta' },
  BAJA: { palabra: 'Baja', tono: 'anulado', icono: 'anular' },
};

/** Qué resultado tuvo cada fila de una importación, con su tono. */
export const RESULTADO_IMPORTACION: Record<string, Vestido> = {
  NUEVO: { palabra: 'Se carga', tono: 'exito', icono: 'check' },
  EXISTENTE: { palabra: 'Ya estaba', tono: 'ausente', icono: 'integrantes' },
  REPETIDO_EN_EL_ARCHIVO: { palabra: 'Repetida en el archivo', tono: 'informacion', icono: 'alerta' },
  ERROR: { palabra: 'No se puede cargar', tono: 'alerta', icono: 'alerta' },
};
